"use client";

import * as React from "react";

// Jobutupaki logo. Concept: an infinity symbol whose two loops are made of
// camera-lens apertures with falling Matrix-style digits weaving through.
// Renders crisply at any size (16px favicon up to a hero illustration).

export function JobutuLogo(props: { size?: number; title?: string }) {
  const size = props.size ?? 32;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 32"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={props.title ?? "Jobutupaki"}
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id="ju-stroke" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#5b8def" />
          <stop offset="55%" stopColor="#7c5bef" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
        <radialGradient id="ju-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(124, 91, 239, 0.45)" />
          <stop offset="100%" stopColor="rgba(124, 91, 239, 0)" />
        </radialGradient>
        {/* Aperture clip path used inside each loop. */}
        <clipPath id="ju-clip-left">
          <circle cx="18" cy="16" r="9" />
        </clipPath>
        <clipPath id="ju-clip-right">
          <circle cx="46" cy="16" r="9" />
        </clipPath>
      </defs>

      <rect x="0" y="0" width="64" height="32" fill="transparent" />
      <ellipse cx="32" cy="16" rx="32" ry="14" fill="url(#ju-glow)" />

      {/* Infinity stroke — two interlocking loops drawn as a single path. */}
      <path
        d="M18 6 C26 6 28 16 32 16 C36 16 38 6 46 6 C54 6 58 10 58 16 C58 22 54 26 46 26 C38 26 36 16 32 16 C28 16 26 26 18 26 C10 26 6 22 6 16 C6 10 10 6 18 6 Z"
        fill="none"
        stroke="url(#ju-stroke)"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Camera apertures: 5 triangular blades inside each loop, rotated
          slightly so they read as a lens. */}
      <g clipPath="url(#ju-clip-left)" opacity="0.85">
        <Aperture cx={18} cy={16} r={6.5} />
      </g>
      <g clipPath="url(#ju-clip-right)" opacity="0.85">
        <Aperture cx={46} cy={16} r={6.5} rotate={20} />
      </g>

      {/* Matrix-rain dots streaming along the curves. */}
      <g fill="#22c55e" opacity="0.85">
        <circle cx="10" cy="10" r="0.85" />
        <circle cx="13" cy="22" r="0.7" />
        <circle cx="22" cy="8"  r="0.6" />
        <circle cx="27" cy="20" r="0.55" />
        <circle cx="32" cy="13" r="0.7" />
        <circle cx="38" cy="9"  r="0.55" />
        <circle cx="44" cy="23" r="0.85" />
        <circle cx="51" cy="11" r="0.6" />
        <circle cx="55" cy="22" r="0.5" />
      </g>
    </svg>
  );
}

function Aperture(props: { cx: number; cy: number; r: number; rotate?: number }) {
  const blades = 5;
  const r = props.r;
  // Aperture iris drawn as a polygon of `blades` overlapping triangles.
  const tris: React.ReactNode[] = [];
  for (let i = 0; i < blades; i++) {
    const a = (i / blades) * Math.PI * 2 + ((props.rotate ?? 0) * Math.PI) / 180;
    const a2 = a + (Math.PI * 2) / blades;
    const x1 = props.cx + Math.cos(a) * r;
    const y1 = props.cy + Math.sin(a) * r;
    const x2 = props.cx + Math.cos(a2) * r;
    const y2 = props.cy + Math.sin(a2) * r;
    tris.push(
      <polygon
        key={i}
        points={`${props.cx},${props.cy} ${x1},${y1} ${x2},${y2}`}
        fill={i % 2 === 0 ? "rgba(124,91,239,0.55)" : "rgba(91,141,239,0.55)"}
        stroke="rgba(255,255,255,0.45)"
        strokeWidth="0.4"
      />,
    );
  }
  return (
    <g>
      {tris}
      <circle cx={props.cx} cy={props.cy} r={r * 0.35} fill="#0c0f1f" />
      <circle cx={props.cx + r * 0.1} cy={props.cy - r * 0.15} r={r * 0.12} fill="rgba(255,255,255,0.85)" />
    </g>
  );
}
